/**
 * PostHog product analytics for the storefront.
 *
 * Loaded by snippets/posthog.liquid (deferred, after the core chain) only when
 * Theme settings → Analytics has a project token. Reads its settings from the
 * data attributes on its own <script id="ww-analytics"> tag.
 *
 * Consent
 *   PostHog initialises opted out, so it stores nothing and sends nothing. It
 *   opts in only when Shopify's Customer Privacy API reports that analytics
 *   processing is allowed: immediately in regions where the merchant requires
 *   no consent, otherwise once the visitor accepts the cookie banner. A later
 *   decline opts it out again and clears what it stored. The banner is the
 *   Pandectes app embed, which writes the choice to Shopify's API; the region
 *   rules live in Shopify (Settings → Customer privacy). This script reads
 *   only the Shopify API, never Pandectes directly.
 *
 * What it sends
 *   $pageview / $pageleave   every page, automatically
 *   add_to_cart              from the theme's cart-update pub/sub event
 *   cart_updated             quantity changes and removals in the cart
 *   gift_guide_*             sent by assets/ww-gift-guide.js through
 *                            window.wwAnalytics
 *   checkout events          NOT from here: theme code cannot run on checkout.
 *                            docs/analytics/posthog-custom-pixel.js is the
 *                            Shopify custom pixel that reports those.
 *
 * Other scripts go through window.wwAnalytics rather than window.posthog, so
 * they work unchanged when analytics is switched off.
 *
 * Event catalogue and setup: docs/analytics/posthog-setup.md.
 */
(function () {
  'use strict';

  const script = document.getElementById('ww-analytics');
  if (!script || window.wwAnalytics) return;

  const settings = script.dataset;
  if (!settings.token) return;

  // localStorage key: the customer id this device was last identified as.
  const CUSTOMER_KEY = 'ww_ph_customer';

  const state = {
    ph: null, // the PostHog instance handed to loaded(); see applyConsent
    allowed: null, // Shopify's latest answer: true, false, or null for unknown
  };

  /* ---------- PostHog loader (official snippet, unchanged) ---------- */
  /* prettier-ignore */
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog&&window.posthog.__loaded)||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}p||((p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",p.onerror=function(){p=null},(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r));var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group identify setPersonProperties setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags resetGroups onFeatureFlags addFeatureFlagsHandler onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);

  window.posthog.init(settings.token, {
    api_host: settings.host,
    defaults: '2026-05-30',
    // Opted out until Shopify says analytics is allowed: every event is
    // dropped. PostHog still creates a visitor id at init and, by default,
    // writes it to its cookie at once, so persistence is opted out as well:
    // an existing cookie is still read, nothing is written until opt-in, and
    // opting out removes it. Verified live 2026-10-03. (Starting in memory
    // persistence instead gave every page load a new visitor id.)
    opt_out_capturing_by_default: true,
    opt_out_persistence_by_default: true,
    // No feature flags or surveys are used, and the /flags call would send
    // the visitor id before consent.
    advanced_disable_flags: true,
    // Anonymous visitors get no person profile; one is created by identify().
    person_profiles: 'identified_only',
    autocapture: settings.autocapture === 'true',
    // Session replay is deliberately off: Microsoft Clarity already records
    // sessions on this store, and the privacy policy is written on that basis.
    disable_session_recording: true,
    // The 2026 defaults debounce storage writes by 250ms. A gift guide card is
    // a plain link, so the session properties registered on its click must be
    // on disk before the product page loads.
    persistence_save_debounce_ms: 0,
    // PostHog calls this before it swaps the global stub for the real
    // instance, so the instance must come from here, never window.posthog:
    // on the stub, has_opted_in_capturing() returns a truthy array length.
    loaded(ph) {
      state.ph = ph;
      applyConsent();
    },
  });

  /* ---------- consent ---------- */

  function optIn(ph) {
    if (ph.has_opted_in_capturing()) return;
    // No "$opt_in" event: in regions without a banner this runs on every
    // page load and would be pure noise.
    ph.opt_in_capturing({ captureEventName: null });
  }

  function applyConsent() {
    const ph = state.ph;
    if (!ph || state.allowed === null) return;

    if (state.allowed) {
      optIn(ph);
      syncCustomer(ph);
    } else if (!ph.has_opted_out_capturing()) {
      // Also disables persistence, which removes the cookie and storage.
      ph.opt_out_capturing();
    }
  }

  function setAllowed(allowed) {
    state.allowed = Boolean(allowed);
    applyConsent();
  }

  function readConsent() {
    const shopify = window.Shopify;
    if (!shopify || typeof shopify.loadFeatures !== 'function') return false;

    shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      // On any failure PostHog simply stays opted out: the safe default.
      if (error || !shopify.customerPrivacy) return;
      setAllowed(shopify.customerPrivacy.analyticsProcessingAllowed());
    });
    return true;
  }

  // Shopify fires this only when consent changes, never on page load, so the
  // current state is read above as well.
  document.addEventListener('visitorConsentCollected', (event) => {
    setAllowed(event.detail && event.detail.analyticsAllowed);
  });

  // Shopify.loadFeatures comes from content_for_header, which precedes this
  // deferred script. The retry is insurance against a layout change that
  // moves this script above it.
  (function waitForShopify(attempt) {
    if (readConsent()) return;
    if (attempt < 50) setTimeout(() => waitForShopify(attempt + 1), 100);
  })(0);

  /* ---------- logged-in customers ---------- */

  function readStoredCustomer() {
    try {
      return localStorage.getItem(CUSTOMER_KEY) || '';
    } catch (error) {
      return '';
    }
  }

  function writeStoredCustomer(id) {
    try {
      if (id) localStorage.setItem(CUSTOMER_KEY, id);
      else localStorage.removeItem(CUSTOMER_KEY);
    } catch (error) {
      // Storage blocked: identification still works for this page.
    }
  }

  // Ties events to the Shopify customer id, nothing more. No name or email
  // leaves the store. reset() also clears PostHog's consent flag, so it is
  // always followed by optIn().
  function syncCustomer(ph) {
    const id = settings.customerId || '';
    const previous = readStoredCustomer();

    if (id) {
      if (previous && previous !== id) {
        ph.reset();
        optIn(ph);
      }
      ph.identify(id);
      writeStoredCustomer(id);
    } else if (previous) {
      // Logged out since the last page: stop crediting this device to them.
      ph.reset();
      optIn(ph);
      writeStoredCustomer('');
    }
  }

  /* ---------- public API for other theme scripts ---------- */

  function capture(name, properties, options) {
    if (!window.posthog || typeof window.posthog.capture !== 'function') return;
    window.posthog.capture(name, properties, options);
  }

  function registerForSession(properties) {
    if (!window.posthog || typeof window.posthog.register_for_session !== 'function') return;
    window.posthog.register_for_session(properties);
  }

  window.wwAnalytics = { capture, registerForSession };

  /* ---------- cart ---------- */

  function money(cents) {
    return typeof cents === 'number' ? cents / 100 : undefined;
  }

  function lineItemProperties(item) {
    return {
      product_id: item.product_id,
      product_title: item.product_title,
      product_handle: item.handle,
      variant_id: item.variant_id || item.id,
      variant_title: item.variant_title,
      sku: item.sku,
      price: money(typeof item.final_price === 'number' ? item.final_price : item.price),
      quantity: item.quantity,
      currency: settings.currency,
    };
  }

  // The cart-update payload differs by source. product-form and quick-add pass
  // the /cart/add.js response: one line item, or { items: [...] } for a bundle.
  // cart-items passes the whole cart from /cart/change.js, recognisable by its
  // item_count, and that is a quantity change or removal rather than an add.
  function trackCartUpdate(data) {
    const cart = data && data.cartData;
    if (!cart) return;

    if (typeof cart.item_count === 'number') {
      capture('cart_updated', {
        item_count: cart.item_count,
        cart_total: money(cart.total_price),
        currency: settings.currency,
        source: data.source || 'unknown',
      });
      return;
    }

    const items = Array.isArray(cart.items) ? cart.items : [cart];
    items.forEach((item) => {
      if (!item || item.id === undefined) return;
      capture('add_to_cart', Object.assign(lineItemProperties(item), { source: data.source || 'unknown' }));
    });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, trackCartUpdate);
  }
})();
